import { useCallback } from "react";
import { removeStorageItem } from "@tellescope/utils";
import { useEnduserStore } from "./index";

// Re-export store for convenience
export { useEnduserStore };

// Hook to initialize enduser session from token
export const useEnduserInitFromToken = () => {
  const initializeFromToken = useEnduserStore((state) => state.initializeFromToken);

  return useCallback(
    async (token: string, businessId?: string) => {
      return await initializeFromToken(token, businessId);
    },
    [initializeFromToken]
  );
};

// Hook for enduser logout
export const useEnduserLogout = () => {
  const logout = useEnduserStore((state) => state.logout);

  return useCallback(async () => {
    await logout();
    removeStorageItem("tellescope-portal-enduser-store");
  }, [logout]);
};

// Individual auth status selectors to prevent object recreation
export const useEnduserIsAuthenticated = () => useEnduserStore((state) => !!state.session?.authToken);
export const useEnduserHasSession = () => useEnduserStore((state) => !!state.session);
export const useEnduserCurrentEnduser = () => useEnduserStore((state) => state.currentEnduser);
export const useEnduserIsLoading = () => useEnduserStore((state) => state.isLoading);
export const useEnduserTokenError = () => useEnduserStore((state) => state.tokenError);
export const useEnduserUpdateCurrentEnduser = () => useEnduserStore((state) => state.updateCurrentEnduser);

// Hook to set token error
export const useSetEnduserTokenError = () => {
  const setTokenError = useEnduserStore((state) => state.setTokenError);
  return useCallback(
    (error: string | null) => {
      setTokenError(error);
    },
    [setTokenError]
  );
};

// Combined auth status hook for convenience
export const useEnduserAuthStatus = () => {
  return {
    isAuthenticated: useEnduserIsAuthenticated(),
    hasSession: useEnduserHasSession(),
    currentEnduser: useEnduserCurrentEnduser(),
    isLoading: useEnduserIsLoading(),
    tokenError: useEnduserTokenError(),
  };
};

// OTP hooks (shared by recovery and MFA flows)
export const useEnduserOtpToken = () => useEnduserStore((state) => state.otpToken);
export const useEnduserOtpMethods = () => useEnduserStore((state) => state.otpMethods);
export const useEnduserOtpSentVia = () => useEnduserStore((state) => state.otpSentVia);
export const useEnduserOtpError = () => useEnduserStore((state) => state.otpError);
export const useEnduserOtpMode = () => useEnduserStore((state) => state.otpMode);
export const useEnduserInitOtpRecovery = () => useEnduserStore((state) => state.initializeOtpRecovery);
export const useEnduserInitOtpForMfa = () => useEnduserStore((state) => state.initializeOtpForMfa);
export const useEnduserSendOtp = () => useEnduserStore((state) => state.sendOtpCode);
export const useEnduserVerifyOtp = () => useEnduserStore((state) => state.verifyOtpCode);
export const useEnduserClearOtp = () => useEnduserStore((state) => state.clearOtpState);

// Login flow hooks
export const useEnduserBeginLogin = () => useEnduserStore((state) => state.beginLoginFlow);
export const useEnduserAuthenticatePassword = () => useEnduserStore((state) => state.authenticateWithPassword);
export const useEnduserVerifyLoginOtp = () => useEnduserStore((state) => state.verifyLoginFlowOtp);
export const useEnduserResendLoginOtp = () => useEnduserStore((state) => state.resendLoginFlowOtp);
export const useEnduserLoginFlowEmail = () => useEnduserStore((state) => state.loginFlowEmail);
export const useEnduserLoginFlowOtpToken = () => useEnduserStore((state) => state.loginFlowOtpToken);
export const useEnduserLoginFlowError = () => useEnduserStore((state) => state.loginFlowError);
export const useEnduserSetLoginFlowError = () => useEnduserStore((state) => state.setLoginFlowError);
export const useEnduserClearLoginFlow = () => useEnduserStore((state) => state.clearLoginFlowState);

// Registration hooks
export const useEnduserRegister = () => useEnduserStore((state) => state.register);

// Password reset hooks
export const useEnduserRequestPasswordReset = () => useEnduserStore((state) => state.requestPasswordReset);
export const useEnduserResetPassword = () => useEnduserStore((state) => state.resetPassword);

// Terms of Service hooks
export const useEnduserAcceptTerms = () => useEnduserStore((state) => state.acceptTerms);
